import { ArrowUpRight, Circle, Code2 } from 'lucide-react';
import { projects } from '../../data/siteContent';
import { useApp } from '../../context/AppContext';
import { Reveal } from '../ui/Reveal';
import { SectionHeading } from '../ui/SectionHeading';

// Project-specific copy may be a brand name or a locale map.
function getProjectCopy(value, language) {
  if (typeof value === 'string') return value;
  return value?.[language] ?? value?.en ?? '';
}

// ProjectArtwork creates branded, fast-loading previews without fragile iframes.
function ProjectArtwork({ project, projectIndex, title }) {
  const Icon = project.icon;

  return (
    <div className={`project-artwork project-artwork--${project.id}`} style={{ '--project-accent': project.accent }}>
      <div className="browser-bar">
        <span><i /><i /><i /></span>
        <small>{project.domain}</small>
        <Circle size={8} fill="currentColor" />
      </div>
      <div className="project-artwork__grid" aria-hidden="true" />
      <div className="project-artwork__icon"><Icon strokeWidth={1.2} /></div>
      <div className="project-artwork__wordmark">{title}</div>
      <div className="project-artwork__code"><Code2 size={13} /> {projectIndex} / AZ</div>
    </div>
  );
}

// Each selected-work card opens the real production destination in a new tab.
export function Projects() {
  const { language, t } = useApp();

  return (
    <section id="work" className="projects-section section-pad">
      <div className="page-shell">
        <SectionHeading kicker={t.projects.kicker} title={t.projects.title} intro={t.projects.intro} light />

        <div className="projects-list">
          {projects.map((project, index) => {
            const projectIndex = project.index ?? String(index + 1).padStart(2, '0');
            const projectTitle = getProjectCopy(project.title, language);
            const projectType = getProjectCopy(project.type, language);
            const projectCta = getProjectCopy(project.cta, language) || t.projects.open;

            return (
              <Reveal delay={index * 0.04} key={project.id}>
                <article className="project-card">
                  <a href={project.url} target="_blank" rel="noreferrer" aria-label={`${t.actions.visit}: ${projectTitle}`}>
                    <ProjectArtwork project={project} projectIndex={projectIndex} title={projectTitle} />
                  </a>
                  <div className="project-meta">
                    <div className="project-meta__top">
                      <span>{projectIndex}</span>
                      <span>{projectType}</span>
                      <span>{project.year}</span>
                    </div>
                    <h3>{projectTitle}</h3>
                    <p>{project.description[language]}</p>
                    <div className="project-stack">
                      {project.stack.map((skill) => <span key={skill}>{skill}</span>)}
                    </div>
                    <a className="project-link" href={project.url} target="_blank" rel="noreferrer">
                      <span><i />{t.projects.live}</span>{projectCta}<ArrowUpRight />
                    </a>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
